// client/src/pages/admin/adminInstructorManagement.jsx

import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import showNotification from '../../utilities/showNotification';
import ProfileHeader from '../../components/headers/profileHeader';
import SharedHeader from '../../components/headers/sharedHeader1';
import CustomButton from '../../components/customs/customButton';
import CustomAlert from '../../components/customs/customAlert';
import BackButton from '../../components/customs/backButton';
import { GlobalContext } from '../../context/provider';
import useInstructor from '../../hooks/useInstructor';
import useRecognizedInstructor from '../../hooks/useRecognizedInstructor';
import useCourse from '../../hooks/useCourse';
import useMessage from '../../hooks/useMessage';

import '../../styles/globalstyles.css';
import './adminInstructorManagement.css';

const AdminInstructorManagement = () => {
  const navigate = useNavigate();
  const { authState: { authData } } = useContext(GlobalContext);
  
  const {
    getAllInstructors,
    createInstructor,
    updateInstructor,
    deleteInstructor,
    assignCourse,
    unassignCourse
  } = useInstructor();

  const {
    getAllRecognizedInstructors,
    createRecognizedInstructor,
    updateRecognizedInstructor,
    deleteRecognizedInstructor
  } = useRecognizedInstructor();

  const { getAllCourses } = useCourse();

  const { 
    getMessagesAndMarkSeen,
    sendMessage,
} = useMessage();

  const [isLoading, setIsLoading] = useState(true);
  const [instructors, setInstructors] = useState([]);
  const [recognizedInstructors, setRecognizedInstructors] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedInstructor, setSelectedInstructor] = useState(null);
  const [selectedRecInstructor, setSelectedRecInstructor] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [modalVisible, setModalVisible] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [assignCourseModalVisible, setAssignCourseModalVisible] = useState(false);
  const [messageModalVisible, setMessageModalVisible] = useState(false);
  const [alertVisible, setAlertVisible] = useState(false);
  
  const [formData, setFormData] = useState({});
  const [assignmentData, setAssignmentData] = useState({
    courseId: '',
    role: 'primary',
    semester: ''
  });
  const [messageData, setMessageData] = useState({
    title: '',
    body: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [instructorsRes, recognizedInstructorsRes, coursesRes] = await Promise.all([
        getAllInstructors(),
        getAllRecognizedInstructors(),  
        getAllCourses(),
      ]);

      console.log('Registered Instructors:', instructorsRes);
      console.log('Recognized Instructors:', recognizedInstructorsRes);

      if (instructorsRes.success) {
        setInstructors(instructorsRes.data || []);
      }
      if (recognizedInstructorsRes.success) {  
        setRecognizedInstructors(recognizedInstructorsRes.data || []);
      }
      if (coursesRes.success) {
        setCourses(coursesRes.data || []);
      }
    } catch (error) {
      showNotification('Failed to load data', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateInstructor = () => {
    setFormData({
      name: '',
      email: '',
    });
    setFormVisible(true);
  };

  const handleEditInstructor = (instructor) => {
    setFormData({
      loginId: instructor.loginId,
      name: instructor.name || '',
      email: instructor.email || '',
      title: instructor.title || '',
      department: instructor.department || '',
      bio: instructor.bio || '',
      expertise: instructor.expertise?.join(', ') || '',
      qualifications: instructor.qualifications?.join(', ') || '',
      phoneNumber: instructor.phoneNumber || '',
      officeLocation: instructor.officeLocation || '',
      officeHours: instructor.officeHours || ''
    });
    setFormVisible(true);
    setModalVisible(false);
  };

  const handleSubmitInstructor = async () => {
    try {
      if (!formData.name || !formData.email) {
        showNotification('Please fill in required fields', 'error');
        return;
      }

      const instructorData = {
        name: formData.name,
        email: formData.email,
        changedById: authData?.id
      };

      let response;
      if (formData.loginId) {
        response = await updateRecognizedInstructor(formData.loginId, instructorData);
      } else {
        response = await createRecognizedInstructor(instructorData);
      }

      if (response.success) {
        showNotification(`Instructor ${formData.loginId ? 'updated' : 'created'} successfully`, 'success');
        setFormVisible(false);
        loadData();
      } else {
        showNotification(response.message || 'Failed to save instructor', 'error');
      }
    } catch (error) {
      showNotification('Failed to save instructor', 'error');
    }
  };

  const handleDeleteInstructor = async () => {
    try {
      const response = await deleteInstructor(selectedInstructor.loginId, authData.id);
      if (response.success) {
        showNotification('Instructor deleted successfully', 'success');
        setAlertVisible(false);
        setModalVisible(false);
        loadData();
      } else {
        showNotification(response.message || 'Failed to delete instructor', 'error');
      }
    } catch (error) {
      showNotification('Failed to delete instructor', 'error');
    }
  };

  const handleAssignCourse = () => {
    setAssignmentData({
      courseId: '',
      role: 'primary',
      assignedById: authData?.id,
      semester: ''
    });
    setAssignCourseModalVisible(true);
  };

  const handleUnassignCourse = async (courseId, unassignedById) => {
    try {
      const response = await unassignCourse(selectedInstructor.loginId, courseId, unassignedById);
      
      if (response.success) {
        showNotification('Course unassigned successfully', 'success');
        loadData();
      } else {
        showNotification(response.message || 'Failed to unassign course', 'error');
      }
    } catch (error) {
      showNotification('Failed to unassign course', 'error');
    }
  };

  const handleSubmitAssignment = async () => {
    try {
      if (!assignmentData.courseId || !assignmentData.semester) {
        showNotification('Please fill in all fields', 'error');
        return;
      }

      const response = await assignCourse(selectedInstructor.loginId, assignmentData);
      
      if (response.success) {
        showNotification('Course assigned successfully', 'success');
        setAssignCourseModalVisible(false);
        loadData();
      } else {
        showNotification(response.message || 'Failed to assign course', 'error');
      }
    } catch (error) {
      showNotification('Failed to assign course', 'error');
    }
  };

  const handleSendMessage = async () => {
    try {
      if (!messageData.title || !messageData.body) {
        showNotification('Please fill in all message fields', 'error');
        return;
      }

      const messagePayload = {
        targetUserId: selectedInstructor.loginId,
        targetName: selectedInstructor.name,
        title: messageData.title,
        body: messageData.body,
        senderUserId: authData.id,
        senderName: authData.name,
        type: 'general',
        priority: 'medium',
        actionRequired: false
      };

      const response = await sendMessage(messagePayload);

      if (response.success) {
        showNotification('Message sent successfully', 'success');
        setMessageModalVisible(false);
        setMessageData({ title: '', body: '' });
      } else {
        showNotification(response.message || 'Failed to send message', 'error');
      }
    } catch (error) {
      showNotification('Failed to send message', 'error');
    }
  };

  const filteredRegisteredInstructors = instructors.filter(instructor => {
    if (!searchQuery) return true; // Show all if no search query
    return (
      instructor.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      instructor.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      instructor.department?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const filteredRecognizedInstructors = recognizedInstructors.filter(instructor => {
    if (!searchQuery) return true; // Show all if no search query
    return (
      instructor.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      instructor.email?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const profileHeaderData = { 
      ...authData, 
      title: '👨‍🏫 Instructor Management',
      // subTitle: `${courseInfo?.courseId}`
  };

  return (
    <div className="main-container">
      <div className="main-content">

        {/* Header */}
        <div className="header-row">
          <ProfileHeader data={profileHeaderData} activePage="admin-instructors" />
          <div className="headerMiddle">
            <img src="/images/prof_ix.png" alt="Logo" className="header-logo" />
          </div>
          <SharedHeader notificationCount={0} icons={[]} />
        </div>

        {/* Tab Navigation */}
        <div className="top-tab-container">
          <BackButton 
            to="/admin/dashboard"
            activeTab="management"
            label="←"
          />
        </div>

        <div className="aim-scroll-container">

          {/* ── Stats Row ── */}
          <div className="aim-stats-row">
            <div className="aim-stat-card aim-stat-total">
              <div className="aim-stat-icon">👨‍🏫</div>
              <div className="aim-stat-info">
                <div className="aim-stat-number">{filteredRegisteredInstructors.length + filteredRecognizedInstructors.length}</div>
                <div className="aim-stat-label">Total Instructors</div>
              </div>
            </div>
            <div className="aim-stat-card aim-stat-registered">
              <div className="aim-stat-icon">✅</div>
              <div className="aim-stat-info">
                <div className="aim-stat-number">{filteredRegisteredInstructors.length}</div>
                <div className="aim-stat-label">Registered</div>
              </div>
            </div>
            <div className="aim-stat-card aim-stat-new">
              <div className="aim-stat-icon">✨</div>
              <div className="aim-stat-info">
                <div className="aim-stat-number">{filteredRecognizedInstructors.length}</div>
                <div className="aim-stat-label">New / Pending</div>
              </div>
            </div>
            <div className="aim-stat-card aim-stat-courses">
              <div className="aim-stat-icon">📚</div>
              <div className="aim-stat-info">
                <div className="aim-stat-number">
                  {filteredRegisteredInstructors.reduce((sum, i) => sum + (i.assignedCourses?.length || 0), 0)}
                </div>
                <div className="aim-stat-label">Course Assignments</div>
              </div>
            </div>
          </div>

          {/* ── Actions Bar ── */}
          <div className="aim-actions-bar">
            <CustomButton
              buttonTitle="➕ Add Instructor"
              action={handleCreateInstructor}
              bgColor="#667eea"
            />
            <input
              type="text"
              placeholder="🔍 Search by name, email or department..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="aim-search-input"
            />
          </div>

          {/* ── Registered Instructors ── */}
          <div className="aim-section">
            <div className="aim-section-header">
              <h3>👨‍🏫 Registered Instructors</h3>
              <span className="aim-section-count">{filteredRegisteredInstructors.length}</span>
            </div>

            {isLoading ? (
              <div className="instructor-loading-container">
                <div className="instructor-loading-spinner" />
                <p className="instructor-loading-text">Loading instructors...</p>
              </div>
            ) : filteredRegisteredInstructors.length > 0 ? (
              <div className="aim-cards-grid">
                {filteredRegisteredInstructors.map((instructor) => (
                  <div
                    key={instructor.loginId}
                    className="aim-instructor-card"
                    onClick={() => {
                      setSelectedInstructor(instructor);
                      setModalVisible(true);
                    }}
                  >
                    {/* Status Badge */}
                    <span className={`aim-status-badge aim-status-${instructor.accountStatus}`}>
                      {instructor.accountStatus}
                    </span>

                    {/* Avatar */}
                    <img
                      src={instructor.imageUrl || '/images/placeholder.png'}
                      alt={instructor.name}
                      className="aim-avatar"
                    />

                    {/* Name */}
                    <h3 className="aim-card-name">
                      {instructor.title && <span className="aim-card-title-prefix">{instructor.title} </span>}
                      {instructor.name}
                    </h3>

                    {/* Department */}
                    <p className="aim-card-dept">
                      {instructor.department || 'No Department'}
                    </p>

                    {/* Chips */}
                    <div className="aim-chips-row">
                      <span className="aim-chip aim-chip-courses">
                        📚 {instructor.assignedCourses?.length || 0} courses
                      </span>
                      {instructor.expertise?.length > 0 && (
                        <span className="aim-chip aim-chip-expertise">
                          🎯 {instructor.expertise[0]}
                        </span>
                      )}
                    </div>

                  </div>
                ))}
              </div>
            ) : (
              <div className="instructor-empty-state">
                <div className="instructor-empty-icon">👨‍🏫</div>
                <h3 className="instructor-empty-title">No Instructors Found</h3>
                <p className="instructor-empty-text">
                  {searchQuery ? 'Try adjusting your search' : 'No instructors registered yet'}
                </p>
              </div>
            )}
          </div>

          {/* ── New / Recognized Instructors ── */}
          <div className="aim-section">
            <div className="aim-section-header">
              <h3>✨ New Instructors</h3>
              <span className="aim-section-count">{filteredRecognizedInstructors.length}</span>
            </div>

            {isLoading ? (
              <div className="instructor-loading-container">
                <div className="instructor-loading-spinner" />
                <p className="instructor-loading-text">Loading...</p>
              </div>
            ) : filteredRecognizedInstructors.length > 0 ? (
              <div className="aim-recognized-list">
                {filteredRecognizedInstructors.map((instructor) => (
                  <div key={instructor.email} className="aim-recognized-item">
                    <div className="aim-recognized-avatar">
                      {instructor.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="aim-recognized-info">
                      <span className="aim-recognized-name">{instructor.name}</span>
                      <p className="aim-recognized-email">{instructor.email}</p>
                    </div>
                    <span className="aim-recognized-badge">New</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="instructor-no-courses">No new instructors pending</p>
            )}
          </div>

        </div>
      </div>

      {/* ── Instructor Detail Modal ── */}
      {modalVisible && selectedInstructor && (
        <div className="modal-overlay" onClick={() => setModalVisible(false)}>
          <div className="modal-content-top" onClick={e => e.stopPropagation()}>

            <div className="modal-header-top">
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <img
                  src={selectedInstructor.imageUrl || '/images/placeholder.png'}
                  alt={selectedInstructor.name}
                  style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.4)' }}
                />
                <div>
                  <h2 style={{ margin: 0 }}>{selectedInstructor.title} {selectedInstructor.name}</h2>
                  <span style={{ fontSize: '13px', opacity: 0.8 }}>{selectedInstructor.department || 'No Department'}</span>
                </div>
              </div>
              <button className="close-button" onClick={() => setModalVisible(false)}>✕</button>
            </div>

            <div className="modal-body">

              {/* Stats */}
              <div className="aim-modal-stats">
                <div className="aim-modal-stat">
                  <div className="aim-modal-stat-icon">📊</div>
                  <div className="aim-modal-stat-info">
                    <div className="aim-modal-stat-value">
                      {selectedInstructor.accountStatus?.toUpperCase()}
                    </div>
                    <div className="aim-modal-stat-label">Status</div>
                  </div>
                </div>
                <div className="aim-modal-stat">
                  <div className="aim-modal-stat-icon">📚</div>
                  <div className="aim-modal-stat-info">
                    <div className="aim-modal-stat-value">
                      {selectedInstructor.assignedCourses?.length || 0}
                    </div>
                    <div className="aim-modal-stat-label">Courses</div>
                  </div>
                </div>
                <div className="aim-modal-stat">
                  <div className="aim-modal-stat-icon">🎯</div>
                  <div className="aim-modal-stat-info">
                    <div className="aim-modal-stat-value">
                      {selectedInstructor.expertise?.length || 0}
                    </div>
                    <div className="aim-modal-stat-label">Expertise Areas</div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="instructor-actions-group" style={{ marginBottom: '24px' }}>
                <CustomButton
                  buttonTitle="📚 Assign Course"
                  action={handleAssignCourse}
                  bgColor="#667eea"
                />
                <CustomButton
                  buttonTitle="🗑️ Delete"
                  action={() => setAlertVisible(true)}
                  bgColor="#ef4444"
                />
              </div>

              {/* Assigned Courses */}
              <div className="instructor-section-header">
                <h4>📚 Assigned Courses</h4>
              </div>
              {selectedInstructor.assignedCourses?.length > 0 ? (
                <div className="instructor-courses-list" style={{ marginBottom: '24px' }}>
                  {selectedInstructor.assignedCourses.map((course, idx) => (
                    <div key={idx} className="instructor-course-item">
                      <div className="instructor-course-info">
                        <strong>{course.title} ({course.courseId})</strong>
                        <p className="instructor-course-meta">
                          {course.role} · {course.semester}
                        </p>
                      </div>
                      <button
                        className="instructor-course-remove-btn"
                        onClick={() => handleUnassignCourse(course.courseId, authData.id)}
                      >
                        ❌ Remove Instructor
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="instructor-no-courses">No courses assigned yet</p>
              )}

              {/* Info */}
              <div className="instructor-section-header">
                <h4>ℹ️ Instructor Information</h4>
              </div>
              <div className="instructor-info-grid">
                <div className="instructor-info-item">
                  <strong>🆔 Login ID:</strong> {selectedInstructor.loginId}
                </div>
                <div className="instructor-info-item">
                  <strong>📧 Email:</strong> {selectedInstructor.email}
                </div>
                <div className="instructor-info-item">
                  <strong>🏢 Department:</strong> {selectedInstructor.department || 'N/A'}
                </div>
                {selectedInstructor.phoneNumber && (
                  <div className="instructor-info-item">
                    <strong>📱 Phone:</strong> {selectedInstructor.phoneNumber}
                  </div>
                )}
                {selectedInstructor.officeLocation && (
                  <div className="instructor-info-item">
                    <strong>📍 Office:</strong> {selectedInstructor.officeLocation}
                  </div>
                )}
                {selectedInstructor.officeHours && (
                  <div className="instructor-info-item">
                    <strong>🕐 Office Hours:</strong> {selectedInstructor.officeHours}
                  </div>
                )}
                {selectedInstructor.expertise?.length > 0 && (
                  <div className="instructor-info-item">
                    <strong>🎯 Expertise:</strong> {selectedInstructor.expertise.join(', ')}
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      )}

      {/* ── Add / Edit Instructor Modal ── */}
      {formVisible && (
        <div className="modal-overlay" onClick={() => setFormVisible(false)}>
          <div className="modal-content-top" onClick={e => e.stopPropagation()}>

            <div className="modal-header-top">
              <h2>{formData.loginId ? '✏️ Edit Instructor' : '➕ Add New Instructor'}</h2>
              <button className="close-button" onClick={() => setFormVisible(false)}>✕</button>
            </div>

            <div className="modal-body">
              <div className="instructor-form-grid">
                <div className="instructor-form-field">
                  <label className="instructor-form-label">
                    Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    className="instructor-form-input"
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., John Doe"
                  />
                </div>
                <div className="instructor-form-field">
                  <label className="instructor-form-label">
                    Email <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    className="instructor-form-input"
                    value={formData.email || ''}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="e.g., john.doe@example.com"
                  />
                </div>
              </div>

              <div className="instructor-form-actions">
                <button className="instructor-btn instructor-btn-primary" onClick={handleSubmitInstructor}>
                  💾 Save Instructor
                </button>
                <button className="instructor-btn instructor-btn-secondary" onClick={() => setFormVisible(false)}>
                  Cancel
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* ── Assign Course Modal ── */}
      {assignCourseModalVisible && (
        <div className="modal-overlay" onClick={() => setAssignCourseModalVisible(false)}>
          <div className="modal-content-top" onClick={e => e.stopPropagation()}>

            <div className="modal-header-top">
              <h2>📚 Assign Course — {selectedInstructor?.name}</h2>
              <button className="close-button" onClick={() => setAssignCourseModalVisible(false)}>✕</button>
            </div>

            <div className="modal-body">
              <div className="instructor-form-grid">
                <div className="instructor-form-field full-width">
                  <label className="instructor-form-label">
                    Course <span className="required">*</span>
                  </label>
                  <select
                    className="instructor-form-select"
                    value={assignmentData.courseId}
                    onChange={(e) => setAssignmentData({ ...assignmentData, courseId: e.target.value })}
                  >
                    <option value="">Select a course</option>
                    {courses.map((course) => (
                      <option key={course.courseId} value={course.courseId}>
                        {course.courseId} — {course.title}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="instructor-form-field">
                  <label className="instructor-form-label">
                    Role <span className="required">*</span>
                  </label>
                  <select
                    className="instructor-form-select"
                    value={assignmentData.role}
                    onChange={(e) => setAssignmentData({ ...assignmentData, role: e.target.value })}
                  >
                    <option value="primary">Primary Instructor</option>
                    <option value="assistant">Assistant</option>
                    <option value="grader">Grader</option>
                  </select>
                </div>
                <div className="instructor-form-field">
                  <label className="instructor-form-label">
                    Semester <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    className="instructor-form-input"
                    value={assignmentData.semester}
                    onChange={(e) => setAssignmentData({ ...assignmentData, semester: e.target.value })}
                    placeholder="e.g., Fall 2025"
                  />
                </div>
              </div>

              <div className="instructor-form-actions">
                <button className="instructor-btn instructor-btn-primary" onClick={handleSubmitAssignment}>
                  ✅ Assign Course
                </button>
                <button className="instructor-btn instructor-btn-secondary" onClick={() => setAssignCourseModalVisible(false)}>
                  Cancel
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {alertVisible && (
        <CustomAlert
          visible={alertVisible}
          title="Delete Instructor"
          icon="🗑️"
          message="Are you sure you want to delete this instructor? This action cannot be undone."
          onConfirm={handleDeleteInstructor}
          onCancel={() => setAlertVisible(false)}
        />
      )}

    </div>
  );

};

export default AdminInstructorManagement;